/** Raggruppa la lista ordini per giorno di arrivo (redesign lista 2026-10-27).
 *
 *  La lista arriva gia' ordinata dal backend (piu' recenti prima): qui si
 *  spezza in blocchi di giorni CONSECUTIVI, senza riordinare niente. Un ordine
 *  senza data valida finisce in un blocco suo, «Senza data», nella posizione in
 *  cui arriva: spostarlo in fondo cambierebbe l'ordine che Davide conosce.
 *
 *  Il giorno e' quello di Roma, come il filtro date del backend: un ordine
 *  delle 00:30 italiane appartiene a quel giorno, non al precedente in UTC. */

export interface GiornoLista<T> {
  /** Chiave unica del blocco: il giorno (AAAA-MM-GG) e la posizione, perche'
   *  lo stesso giorno puo' tornare dopo un blocco «Senza data». */
  chiave: string;
  /** Il giorno AAAA-MM-GG, o ``null`` per gli ordini senza data. */
  giorno: string | null;
  /** «Oggi», «Ieri» o il giorno per esteso: «sabato 25 ottobre». */
  titolo: string;
  /** La data per esteso accanto a «Oggi»/«Ieri»; vuota negli altri giorni. */
  sottotitolo: string;
  ordini: T[];
}

const ZONA = "Europe/Rome";

function giornoIso(t: number): string {
  // sv-SE formatta come AAAA-MM-GG: una data confrontabile come stringa.
  return new Date(t).toLocaleDateString("sv-SE", { timeZone: ZONA });
}

function perEsteso(iso: string, conAnno: boolean): string {
  // Mezzogiorno UTC: lo stesso giorno a Roma in ogni stagione.
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("it-IT", {
    timeZone: ZONA,
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(conAnno ? { year: "numeric" } : {}),
  });
}

export function raggruppaPerGiorno<T>(
  ordini: readonly T[],
  dataDi: (o: T) => string | null | undefined,
  adesso: number = Date.now(),
): GiornoLista<T>[] {
  const oggi = giornoIso(adesso);
  const ieri = giornoIso(adesso - 86_400_000);
  const annoCorrente = oggi.slice(0, 4);
  const blocchi: GiornoLista<T>[] = [];
  for (const ordine of ordini) {
    const raw = dataDi(ordine);
    const t = raw ? Date.parse(raw) : NaN;
    const giorno = Number.isFinite(t) ? giornoIso(t) : null;
    const ultimo = blocchi[blocchi.length - 1];
    if (ultimo && ultimo.giorno === giorno) {
      ultimo.ordini.push(ordine);
      continue;
    }
    let titolo = "Senza data";
    let sottotitolo = "";
    if (giorno !== null) {
      const esteso = perEsteso(giorno, giorno.slice(0, 4) !== annoCorrente);
      if (giorno === oggi) [titolo, sottotitolo] = ["Oggi", esteso];
      else if (giorno === ieri) [titolo, sottotitolo] = ["Ieri", esteso];
      else titolo = esteso;
    }
    const chiave = `${giorno ?? "senza-data"}-${blocchi.length}`;
    blocchi.push({ chiave, giorno, titolo, sottotitolo, ordini: [ordine] });
  }
  return blocchi;
}
